import toast from "react-hot-toast";
import moment from "moment/moment";
import {
  createTopupOrder,
  getBillingBalance,
  subscribeToPlan,
  verifySubscriptionPayment,
  verifyTopupPayment,
} from "../../api/context/billing";
import { loadRazorpay } from "../../utils/loadRazorpay";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const getPrefill = () => {
  const user = JSON.parse(localStorage.getItem("user")) || {};
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  return {
    email: user?.email || "",
    name: name || user?.email || "",
  };
};

export const buyTopupPack = async (packCode, { onSuccess, onDismiss } = {}) => {
  const sdkReady = await loadRazorpay();
  if (!sdkReady) {
    toast.error("Payment gateway failed to load. Check your connection.");
    return;
  }

  let orderData;
  try {
    const res = await createTopupOrder(packCode);
    orderData = res?.data?.data;
    if (!orderData?.order?.id) {
      toast.error(res?.data?.message || "Could not create order");
      return;
    }
  } catch (err) {
    toast.error(err?.response?.data?.message || "Could not create order");
    return;
  }

  const { order, pack, razorpayKeyId } = orderData;
  const prefill = getPrefill();

  const rzp = new window.Razorpay({
    key: razorpayKeyId,
    order_id: order.id,
    amount: order.amount,
    currency: order.currency,
    name: "Getto — AI Listing Top-up",
    description: `${pack.listingCount} AI credits`,
    prefill,
    theme: { color: "#008695" },
    handler: async (paymentResponse) => {
      try {
        await verifyTopupPayment(paymentResponse);
        toast.success(`${pack.listingCount} credit${pack.listingCount === 1 ? "" : "s"} added to your wallet.`);
        if (onSuccess) onSuccess();
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            "Payment received but verification failed. Please refresh."
        );
      }
    },
    modal: {
      ondismiss: () => {
        toast("Payment cancelled");
        if (onDismiss) onDismiss();
      },
    },
  });
  rzp.open();
};

const formatScheduledDate = (isoDate) => {
  if (!isoDate) return "";
  const d = moment(isoDate);
  return d.isValid() ? d.format("MMM D, YYYY") : "";
};

export const subscribe = async (
  planCode,
  planName,
  { onSuccess, onDismiss } = {}
) => {
  const sdkReady = await loadRazorpay();
  if (!sdkReady) {
    toast.error("Payment gateway failed to load. Check your connection.");
    return;
  }

  let subData;
  try {
    const res = await subscribeToPlan(planCode);
    subData = res?.data?.data;
    if (!subData?.checkout?.subscription_id) {
      toast.error(res?.data?.message || "Could not start subscription");
      return;
    }
  } catch (err) {
    const status = err?.response?.status;
    const data = err?.response?.data;
    if (status === 409) {
      toast.error(
        data?.message ||
          "This plan can't be started right now. Please try again in a moment.",
        { duration: 6000 }
      );
    } else {
      toast.error(data?.message || "Could not start subscription");
    }
    return;
  }

  const { checkout } = subData;
  const isScheduled = !!subData?.isScheduled;
  const scheduledDate = formatScheduledDate(subData?.scheduledStartAt);
  const backendMessage = subData?.message;
  const prefill = getPrefill();

  const rzp = new window.Razorpay({
    key: checkout.razorpayKeyId,
    subscription_id: checkout.subscription_id,
    name: "Getto — AI Listing Subscription",
    description: `${planName} plan`,
    prefill,
    theme: { color: "#008695" },
    handler: async (paymentResponse) => {
      let verifyMessage = null;
      let verifiedActive = false;
      try {
        const verifyRes = await verifySubscriptionPayment({
          razorpay_payment_id: paymentResponse?.razorpay_payment_id,
          razorpay_subscription_id: paymentResponse?.razorpay_subscription_id,
          razorpay_signature: paymentResponse?.razorpay_signature,
        });
        const verifyData = verifyRes?.data?.data;
        verifyMessage = verifyRes?.data?.message || verifyData?.message;
        verifiedActive =
          verifyData?.balance?.subscription?.status === "active";
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            "Payment received but verification failed. Please refresh."
        );
      }

      if (isScheduled) {
        toast.success(
          verifyMessage ||
            backendMessage ||
            (scheduledDate
              ? `${planName} will start on ${scheduledDate} — your current plan continues until then.`
              : `${planName} will start when your current plan ends.`),
          { duration: 6000 }
        );
        if (onSuccess) onSuccess(true);
        return;
      }

      if (verifiedActive) {
        toast.success(verifyMessage || backendMessage || `You're now on ${planName}!`);
        if (onSuccess) onSuccess(true);
        return;
      }

      toast("Subscription submitted — activating...");
      const active = await pollBalanceUntilActive();
      if (active) {
        toast.success(verifyMessage || backendMessage || `You're now on ${planName}!`);
      } else {
        toast("Subscription is being processed. Refresh in a minute.");
      }
      if (onSuccess) onSuccess(active);
    },
    modal: {
      ondismiss: () => {
        toast("Checkout closed — your current plan is unchanged");
        if (onDismiss) onDismiss();
      },
    },
  });
  rzp.open();
};

const pollBalanceUntilActive = async (maxTries = 10) => {
  for (let i = 0; i < maxTries; i++) {
    await sleep(3000);
    try {
      const res = await getBillingBalance();
      if (res?.data?.data?.subscription?.status === "active") return true;
    } catch (e) {}
  }
  return false;
};
